// THE APPLICATION LANGUAGE — the language of the console itself (labels, verbs, toasts), kept
// distinct from the language of the RECORD (a person's correspondence language, which lives on
// persons.language and governs content, not chrome).
//
// The catalogue is keyed by the ENGLISH TEXT ITSELF: `t('Hold')` renders "Hold" in English and
// looks up "Übernehmen" in German. English therefore needs no catalogue, and a missing German
// entry falls back to English rather than to a key — nothing ever renders as `btn.hold`.
//
// Every page carries its own catalogues beside it (`<Page>.de.ts`, `<Page>.fr.ts`) and
// registers them on import, so a dozen pages can be translated in parallel without one shared
// file to fight over.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { intlLocale, isUiLang, makeT, makeTp, mergeDict, UI_LANGS, type Dict, type UiLang, type Vars } from '../../shared/i18n.js';

const STORAGE_KEY = 'crm.lang';

type CatalogLang = Exclude<UiLang, 'en'>;
const CATALOGS: Record<CatalogLang, Dict> = { de: {}, fr: {} };

export function registerCatalog(lang: CatalogLang, dict: Dict, source?: string): void {
  mergeDict(CATALOGS[lang], dict, source);
}
export function registerDe(dict: Dict, source?: string): void { registerCatalog('de', dict, source); }
export function registerFr(dict: Dict, source?: string): void { registerCatalog('fr', dict, source); }

interface Locale {
  lang: UiLang;
  setLang: (l: UiLang) => void;
  t: (key: string, vars?: Vars) => string;
  tp: (n: number, one: string, other: string, vars?: Vars) => string;
  /** 2026-08-23 → 23.08.2026. `null` → '—'; a missing date is never a blank cell. */
  fmtDate: (iso: string | null | undefined) => string;
  fmtDateTime: (iso: string | null | undefined) => string;
  fmtNum: (n: number | string | null | undefined, digits?: number) => string;
  /** 'CHF 12’500' — the Swiss group separator, in every locale this console speaks. */
  fmtMoney: (amount: number | string | null | undefined, currency?: string | null) => string;
}

const fallbackT = makeT(null);
const LocaleContext = createContext<Locale>({
  lang: 'en',
  setLang: () => {},
  t: fallbackT,
  tp: makeTp(fallbackT),
  fmtDate: (s) => (s ? s.slice(0, 10) : '—'),
  fmtDateTime: (s) => s ?? '—',
  fmtNum: (n) => String(n ?? '—'),
  fmtMoney: (a) => String(a ?? '—'),
});

function initialLang(): UiLang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isUiLang(stored)) return stored;
  } catch { /* private mode */ }
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'en';
  return isUiLang(nav) ? nav : 'en';
}

export function LocaleProvider({ children }: { children: ReactNode; authed?: boolean }) {
  const [lang, setLangState] = useState<UiLang>(initialLang);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setLang = useCallback((l: UiLang) => setLangState(l), []);

  const value = useMemo<Locale>(() => {
    const t = makeT(lang === 'en' ? null : CATALOGS[lang]);
    const loc = intlLocale(lang);
    const dateF = new Intl.DateTimeFormat(loc, { day: '2-digit', month: '2-digit', year: 'numeric' });
    const dateTimeF = new Intl.DateTimeFormat(loc, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const parse = (iso: string): Date | null => {
      const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    // Swiss grouping in every locale: the money in this record IS Swiss.
    const group = (n: number, digits: number): string =>
      new Intl.NumberFormat('de-CH', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
    return {
      lang,
      setLang,
      t,
      tp: makeTp(t),
      fmtDate: (iso) => { if (!iso) return '—'; const d = parse(iso); return d ? dateF.format(d) : iso; },
      fmtDateTime: (iso) => { if (!iso) return '—'; const d = parse(iso); return d ? dateTimeF.format(d) : iso; },
      fmtNum: (n, digits = 0) => (n === null || n === undefined || n === '' ? '—' : group(Number(n), digits)),
      fmtMoney: (a, currency) => (a === null || a === undefined || a === '' ? '—' : `${currency ?? 'CHF'} ${group(Number(a), 0)}`),
    };
  }, [lang, setLang]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = (): Locale => useContext(LocaleContext);
export const useT = (): Locale['t'] => useContext(LocaleContext).t;

/** EN | DE | FR — one control, in the topbar beside the theme toggle. */
export function LangSwitch() {
  const { lang, setLang } = useLocale();
  return (
    <span className="lang-switch" role="group" aria-label="Language">
      {UI_LANGS.map((l) => (
        <button
          key={l}
          type="button"
          className={`lang-switch-btn${lang === l ? ' on' : ''}`}
          aria-pressed={lang === l}
          onClick={() => setLang(l)}
        >{l.toUpperCase()}</button>
      ))}
    </span>
  );
}
